import React, {
  Fragment, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Grid, MenuItem, TextField, Theme, ThemeProvider, useTheme,
} from '@mui/material';
import { Box } from '@mui/system';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Trans, useTranslation } from 'react-i18next';
import { unwrapResult } from '@reduxjs/toolkit';
import { makeStyles } from '@mui/styles';
import { StyledSelect, styledSelectMenuProps } from '../common/StyledFormInputs';
import { ContentTheme } from './Dashboard/Content/ContentTheme';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  ResetContentList,
  selectContentList,
  selectGetContentListState,
  selectHasMoreContent,
  UpdateHasMoreContent,
  UpdateIsContentPreview,
  UpdatePageNumber,
} from '../store/slices/contentSlice';
import { Content, MetaItem } from '../common/models/content/Content';
import { getPersonalizedContent } from '../store/thunks/contentThunk';
import { selectMemberSession } from '../store/slices/onboardingSlice';
import { getHeaderImageUrl, getHeading } from './Dashboard/Content/ContentCommonFunctions';
import { getRandomInteger } from '../common/RandomNumberHelper';
import { HOME_ROUTE } from '../routes/namedRoutes';
import ContentHorizontalCardSkeleton from './Dashboard/Content/ContentHorizontalCardSkeleton';
import ContentCard from './Dashboard/Content/ContentCard';

interface ContentHomeProps {
  onlySavedContent?: boolean;
  searchTerm?: string;
  topic?: string;
  businessGoal?: string;
  showSearchBar?: boolean;
  fullWidthContainer?: boolean
  setHasNoResults?: (value: boolean) => void
}

const makeOverrideStyle = makeStyles((theme: Theme) => ({
  root: {
    '& #readMoreButton': {
      display: 'none',
    },
    '& #podcast-listen-subscribe-sheet': {
      display: 'none',
    },
    '& .MuiTypography-h2': {
      fontSize: 12,
    },
    '& .MuiCardContent-root': {
      paddingLeft: theme.spacing(1),
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& #content-hero-banner': {
      display: 'none',
    },
    '& #content-footer-view': {
      display: 'none',
    },
  },
}));

/**
 * See PromotionStrategies
 */
const PromoteAllHomeServices = 'promoteAllHomeServices';
const PromoteUserTrades = 'promoteUserTrades';
const PromoteLatestAndUserTrades = 'promoteLatestAndUserTrades';

export default function PersonalizeContent({
  onlySavedContent,
  searchTerm,
  topic,
  businessGoal,
  fullWidthContainer = false,
}: ContentHomeProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [hideEndMessage, setHideEndMessage] = React.useState(false);
  const storeContentList = useAppSelector(selectContentList);
  const getContentListState = useAppSelector(selectGetContentListState);
  const storeHasMoreContent = useAppSelector(selectHasMoreContent);
  const [contentList, setContentList] = useState(storeContentList);
  const [hasMoreContent, setHasMoreContent] = useState(storeHasMoreContent);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = 25;
  const navigate = useNavigate();
  const memberSession = useAppSelector(selectMemberSession);
  const [algorithm, setAlgorithm] = useState<string | undefined>(searchParams.get('algorithm') as string | undefined);
  const [memberId, setMemberId] = useState(searchParams.get('memberId'));
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [showSavedItemsEmptyState, setShowSavedItemsEmptyState] = useState(false);
  const endMessageRef = useRef(null);
  /**
     * Use a new random integer for each page refresh,
     * this will make sure each time different content will be fetched from the backend.
     * When user scrolls down, the same random seed will be used to make sure content
     * is consistent across different pages.
     */
  const [randomContentSeed] = useState(() => getRandomInteger(1, 10000));

  useEffect(() => {
    if (!memberSession.memberEmail || !memberSession.memberEmail.includes('@evercommerce.com')) {
      navigate(HOME_ROUTE);
    }
  }, [memberSession.memberEmail, navigate]);

  useEffect(() => {
    setSearchParams({
      memberId: memberId || '',
      algorithm: algorithm || '',
    });
  }, [memberId, algorithm, setSearchParams]);

  useEffect(() => {
    dispatch(UpdateIsContentPreview(false));
    setContentList(storeContentList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeContentList]);

  const formatedContentList = useMemo(() => contentList.map((content) => {
    const metadata: MetaItem[] = [];

    for (const item of (content.attributes?.metadata || [])) {
      metadata.push({
        id: item.id,
        name: item.name,
        category: 'Topic',
      });
    }

    if (content?.attributes?.content_collection?.data) {
      metadata.push({
        id: 'content_collection',
        name: content?.attributes?.content_collection?.data?.attributes?.title ?? '',
        category: 'Topic',
      });
    } else {
      metadata.push({
        id: 'content_owner',
        name: content?.attributes?.content_owner?.data?.attributes?.display_name ?? '',
        category: 'Topic',
      });
    }

    const { personalize: personalizeMeta } = (content as unknown as {
      personalize?: {
        score?: number,
        promotionName?: string
      }
    });

    return {
      ...content,
      attributes: {
        ...content.attributes ?? {},
        metadata,
        title: content.attributes.title +
          (personalizeMeta ? ` (${personalizeMeta?.score})` : '') +
          (personalizeMeta?.promotionName ? ` (${personalizeMeta?.promotionName})` : ''),
      },
    };
  }), [contentList]);

  useEffect(() => {
    if (onlySavedContent && contentList.length === 0) {
      setShowSavedItemsEmptyState(true);
    } else {
      setShowSavedItemsEmptyState(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentList]);

  useEffect(() => {
    setHasMoreContent(storeHasMoreContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeHasMoreContent]);

  useEffect(() => {
    setInitialFetchDone(false);
    dispatch(ResetContentList([])); // Reset content list
    let promotionStrategy: string | undefined;
    if (algorithm === PromoteAllHomeServices) {
      promotionStrategy = PromoteAllHomeServices;
    } else if (algorithm === PromoteUserTrades) {
      promotionStrategy = PromoteUserTrades;
    } else if (algorithm === PromoteLatestAndUserTrades) {
      promotionStrategy = PromoteLatestAndUserTrades;
    }

    dispatch(
      getPersonalizedContent({
        memberId: memberId || memberSession.memberId,
        legacy: algorithm === 'legacy',
        promotionStrategy,
        randomSeed: randomContentSeed,
      }), // Pass version
    )
      .then(unwrapResult)
      .then((response) => {
        setHideEndMessage(false);
        const res = response as Content[];

        if (!(searchTerm || topic || businessGoal)) {
          dispatch(UpdatePageNumber(2));
          dispatch(UpdateHasMoreContent(res.length === pageSize));
        } else {
          dispatch(UpdatePageNumber(1));
          dispatch(UpdateHasMoreContent(false));
        }
      })
      .catch(() => {
        setHideEndMessage(true);
        dispatch(UpdateHasMoreContent(false));
      })
      .finally(() => {
        setInitialFetchDone(true);
      });
  }, [
    searchTerm,
    businessGoal,
    topic,
    memberId,
    algorithm,
    dispatch,
    memberSession.memberId,
    randomContentSeed,
  ]);

  const overrideStyleClass = makeOverrideStyle(theme);

  const FlattenCard = (contentItem: Content) => (
    <Fragment key={`fragment-${contentItem.id}`}>
      <Grid item key={contentItem.id}
        sx={{
          width: '100%',
          paddingTop: theme.spacing(1),
          paddingLeft: fullWidthContainer ? undefined : theme.spacing(3),
        }}
      >
        <Box
          id={`optimus-content-${contentItem.id}`}
          sx={{
            borderRadius: theme.spacing(1.5),
            border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
            boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 0px 0px rgba(0, 0, 0, 0.00), 0px 0px 0px 0px rgba(0, 0, 0, 0.00)',
          }}
          className={overrideStyleClass.root}
        >
          <ContentCard
            contentId={contentItem.id}
            contentText={contentItem.attributes?.title}
            contentShortDescription={''}
            categoryText={contentItem.attributes?.type}
            // categoryColor={theme.palette.primary.main}
            authorTitle={getHeading(contentItem)}
            publishedDate={contentItem.attributes?.source_published_date}
            // authorText={contentItem.attributes?.banner?.short_description}
            logo={getHeaderImageUrl(contentItem)}
            contentLikeStatus={contentItem.likeStatus}
            formId={contentItem.attributes?.form_id}
            metadata={contentItem.attributes?.metadata}
          />
        </Box>
      </Grid>
    </Fragment>);

  return (
    <ThemeProvider theme={ContentTheme}>
      <Box width={'100%'} style={{ paddingLeft: theme.spacing(3) }}>
        <TextField placeholder="MemberID" value={memberId}
          onChange={(e) => setMemberId(e.target.value as string)} />
        <StyledSelect label="Algorithm" value={algorithm} onChange={(e) => setAlgorithm(e.target.value as string)} MenuProps={styledSelectMenuProps}>
          <MenuItem value={'legacy'}>Legacy</MenuItem>
          <MenuItem value={PromoteAllHomeServices}>
            Personalize promote all home services
          </MenuItem>
          <MenuItem value={PromoteUserTrades}>
            Personalize promote trades</MenuItem>
          <MenuItem value={PromoteLatestAndUserTrades}>
            Personalize promote trades within 60 days
          </MenuItem>
        </StyledSelect>
      </Box>
      <Box width={'100%'}>
        {formatedContentList && <InfiniteScroll
          scrollThreshold={0.8}
          next={() => ([])}
          // This is important field to render the next data
          dataLength={formatedContentList?.length}
          hasMore={hasMoreContent}
          style={{
            overflow: 'hidden',
            display: 'flex',
            flexWrap: 'wrap',
          }}
          loader={<ContentHorizontalCardSkeleton />}
          endMessage={!hideEndMessage && !showSavedItemsEmptyState &&
            <p id='endMessage' style={{
              textAlign: 'center',
              fontSize: theme.spacing(2.25),
              marginTop: theme.spacing(12.5),
              color: theme.palette.grey[500],
            }}
            ref={endMessageRef}>
              {!onlySavedContent &&
                <b>
                  <Trans
                    i18nKey="content.feed.endMessage"
                    components={{
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      topAnchorLink: <a href="#top-anchor"
                        aria-label={t('content.feed.useSearch') || ''}/>,
                    }}
                  />
                </b>
              }
            </p>
          }
        >
          {
            (getContentListState === 'pending' && !initialFetchDone) ?
              <ContentHorizontalCardSkeleton /> :
              formatedContentList?.map(FlattenCard)
          }
        </InfiniteScroll>
        }
      </Box>
    </ThemeProvider>);
}
